import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import LogoutPage from '@/pages/LogoutPage';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import Platforms from '@/pages/Platforms';
import Reports from '@/pages/Reports';
import Audit from '@/pages/Audit';
import Campaigns from '@/pages/Campaigns';
import Settings from '@/pages/Settings';
import AIChat from '@/pages/AIChat';
import AIActions from '@/pages/AIActions';
import Pricing from '@/pages/Pricing';
import Onboarding from '@/pages/Onboarding';
import SuperAdminPage from '@/pages/SuperAdminPage';
import GovernanceConsole from '@/pages/GovernanceConsole';
import MarketIntelligence from '@/pages/MarketIntelligence';
import TaxonomyBuilder from '@/pages/TaxonomyBuilder';
import UTMBuilder from '@/pages/UTMBuilder';
import UserManagement from '@/pages/UserManagement';
import ProtectedRoute from '@/components/ProtectedRoute';
import { ThemeProvider } from '@/components/ThemeProvider';
import { BetaProvider } from '@/components/BetaProvider';
import { AuthProvider } from '@/context/AuthContext';
import '@/App.css';

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id in URL fragment synchronously (before useEffect)
  // This prevents race conditions by processing auth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/platforms" element={<ProtectedRoute><Platforms /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><Audit /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
      <Route path="/ai-chat" element={<ProtectedRoute><AIChat /></ProtectedRoute>} />
      <Route path="/ai-actions" element={<ProtectedRoute><AIActions /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="super_admin"><SuperAdminPage /></ProtectedRoute>} />
      <Route path="/governance" element={<ProtectedRoute requiredRole="super_admin"><GovernanceConsole /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute requiredRole="super_admin"><UserManagement /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><MarketIntelligence /></ProtectedRoute>} />
      <Route path="/taxonomy" element={<ProtectedRoute><TaxonomyBuilder /></ProtectedRoute>} />
      <Route path="/utm-builder" element={<ProtectedRoute><UTMBuilder /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark">
      <AuthProvider>
        <BetaProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster />
          </div>
        </BetaProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
